import { createRouter, createWebHistory } from 'vue-router'
import Layout from '../views/Layout/index.vue'
import HomeView from '../views/HomeView/index.vue'
import SearchView from '../views/SearchView/index.vue'
import Offer from '../views/OfferView/index.vue'
import HelpCenterView from '../views/HelpCenterView/index.vue'
import GuideView from '../views/GuideView/index.vue'
import Carts from '../views/Carts/index.vue'
import Orders from '../views/Orders/index.vue'
import Account from '../views/Account/index.vue'
import H5BankPayment from '../views/BankPayment/H5BankPayment/index.vue'
import BankPayment from '../views/BankPayment/PCBankPayment/index.vue'
import FeaturedView from '@/views/Featured/index.vue'
import Favorites from '@/views/Favorites/index.vue'
import Factory from '@/views/Factory/index.vue'
import { setThemeColor, buriedPointUtil, gaAnalytics } from '@/utils/utils'
import store from '@/store/index.js'
import { useUserStore } from '@/pinia/user'
import { deviceType } from '@/utils/constants'

const isMobile = deviceType === 'MOBILE'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      component: Layout,
      children: [
        {
          path: '',
          name: 'home',
          component: HomeView,
          meta: {
            hideH5Header: true,
            themeColor: '#052785'
          }
        },
        {
          path: '/search',
          name: 'search',
          component: SearchView,
          meta: {
            themeColor: '#052785'
          }
        },
        {
          path: '/featured/:category/:collectionId?',
          name: 'featured',
          component: FeaturedView,
          meta: {
            themeColor: '#052785'
          }
        },
        {
          path: 'offer/:id',
          name: 'offer',
          component: Offer,
          meta: {
            themeColor: '#052785'
          }
        },
        {
          path: '/help',
          name: 'help',
          component: HelpCenterView,
          meta: {
            themeColor: '#052785'
          }
        },
        {
          path: '/guide',
          name: 'guide',
          component: GuideView,
          meta: {
            themeColor: '#052785'
          }
        },
        {
          path: '/carts',
          name: 'carts',
          component: Carts,
          meta: {
            auth: true,
            hideH5Header: true,
            themeColor: '#000000'
          }
        },
        {
          path: '/orders',
          name: 'orders',
          component: Orders,
          meta: {
            auth: true,
            hideH5Header: true,
            themeColor: '#141414'
          }
        },
        {
          path: '/account',
          name: 'account',
          component: Account,
          meta: {
            auth: true,
            hideH5Header: true,
            themeColor: '#141414'
          }
        },
        {
          path: '/bankPayment/:orderNo',
          name: 'bankPayment',
          component: isMobile ? H5BankPayment : BankPayment,
          meta: {
            auth: true,
            hideH5Header: true,
            themeColor: '#141414'
          }
        },
        {
          path: '/favorites',
          name: 'favorites',
          component: Favorites,
          redirect: '/favorites/products',
          children: [
            {
              path: 'products',
              name: 'favoritesProducts',
              component: () => import('@/views/Favorites/products.vue')
            },
            {
              path: 'factories',
              name: 'favoritesFactories',
              component: () => import('@/views/Favorites/factories.vue')
            }
          ],
          meta: {
            auth: true,
            hideH5Header: true,
            themeColor: '#141414'
          }
        }, {
          path: '/favorites/factories/search',
          name: 'favoritesFactoriesSearch',
          component: () => import('@/views/Favorites/factories-search.vue')
        }, {
          path: '/favorites/products/search',
          name: 'favoritesProductsSearch',
          component: () => import('@/views/Favorites/products-search.vue')
        }, {
          path: '/factory/:name/:id',
          name: 'factory',
          component: Factory,
          meta: {
            hideH5Header: true,
            themeColor: '#B0C7F5'
          }
        }, {
          path: '/factory/:name/:id/search',
          name: 'factorySearch',
          component: () => import('@/views/Factory/search.vue'),
          meta: {
            hideH5Header: true,
            themeColor: '#B0C7F5'
          }
        },
        {
          path: '/error/network',
          name: 'networkError',
          component: () => import('@/views/Error/network.vue'),
          meta: {
            themeColor: '#052785'
          }
        },
        {
          path: '/error/invalid',
          name: 'invalidError',
          component: () => import('@/views/Error/invalid.vue'),
          meta: {
            themeColor: '#052785'
          }
        }
      ]
    },
    {
      path: '/notSupport',
      name: 'notSupport',
      component: () => import('@/views/Error/not-support.vue')
    },
    {
      path: '/redirect/sso',
      name: 'ssoRedirect',
      component: () => import('@/views/Redirect/sso.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: {
        name: 'home'
      }
    }
  ],
  scrollBehavior (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

router.beforeEach((to, from, next) => {
  const { tk, uid } = useUserStore().userInfo

  const isLogin = tk != null

  if (isMobile) {
    buriedPointUtil({ eventId: 'MPWH101' })
    gaAnalytics('mp_page_view', 'mp_page_view', {
      bdUserId: uid
    })
    if (to.path === '/search') {
      buriedPointUtil({ eventId: 'MPSC101' })
    }
    if (to.path === '/' && store.state.productList.length === 0) {
      store.dispatch('initHomeData', {
        adsWord: to.query.data
      })
    }
  }

  if (!isLogin) {
    if (to.meta.auth) {
      const utmParams = Object.keys(from.query).filter(key => key.startsWith('utm_') || key === 'source')
      next({
        name: 'guide',
        query: utmParams.reduce((params, cur) => ({ ...params, [cur]: from.query[cur] }), {})
      })
      return
    }

    /**
     * 当路径是 guide 时，保存之前的路由链接，在登录完成后跳转回原路由
     */
    if (to.name === 'guide') {
      /**
       * 避免出现多个异步接口触发 401 导致 guide 页 fallback 被覆盖问题
       */
      if (from.name === 'guide') return

      if (to.query.fallback) {
        next()
        return
      }
      const _from = to.redirectedFrom ?? from
      next({
        name: 'guide',
        params: _from.params,
        query: {
          ..._from.query,
          ...to.query,
          fallback: _from.path ?? '/'
        }
      })
      return
    }
  } else {
    if (to.name === 'guide' && to.query.sp !== '1') {
      next('/')
      return
    }
  }

  next()
})

const updateThemeColor = (route) => {
  const themeColor = route.meta.themeColor
  if (themeColor) setThemeColor(themeColor)
}

router.afterEach((to) => {
  if (isMobile) {
    updateThemeColor(to)
  }
})

export default router
